import { addMinutes, format, isValid, parse } from "date-fns";
import { icon, label, number } from "../config/constants";
import { splitString } from "../utils";

export const HREF_REGEX = /href\s*=\s*(['"])(https?:\/\/.+?)\1/g;

export const SMART_CHIP_REGEX = /^ss_\d+_\d+_\w+_\w+$/;

export const TASK_CHIP_REGEX = /task_(\d+)/;

export const ANCHOR_TAG_REGEX = /<a\s+[^>]*href\s*=\s*(['"])(https?:\/\/[^\/]+)\/?(.*?)\1[^>]*>(.*?)<\/a>/g;

export const NON_EDITABLE_REGEX = /^ss[\w-]*$/;

export const TARGET_BLANK_REGEX = /target\s*=\s*(['"])_blank\1/;

/**
 * Function to generate a unique ID based on provided parameters.
 * @param {number} userID - The user ID.
 * @param {string} smartChipType - The type of smart chip.
 * @param {string} selectedDetails - The selected details related to the smart chip.
 * @returns {string} - Returns a unique ID.
 * @author Shivam Mishra
 */
export const generateUniqueId = (userID, smartChipType, selectedDetails) => {
  const timestamp = Date.now();
  const uniqueId = `ss_${userID}_${timestamp}_${smartChipType}_${selectedDetails}`;
  return uniqueId;
};

/**
 * Function to generate an array representing time slots in 15-minute intervals.
 * @returns {string[]} - Returns an array of time slots formatted as HH:mm.
 * @author Shivam Mishra
 */
export function generateTimeArray() {
  const startTime = new Date();
  startTime.setHours(0, 0, 0, 0); // Set the start time to midnight

  return Array.from({ length: 96 }, (_, index) => {
    const time = addMinutes(startTime, index * 15);
    const formattedTime = format(time, "hh:mm a");
    return formattedTime;
  });
}

/**
 * Function to format a date.
 * @param {Date} date - The date to format.
 * @returns {string} - Returns the formatted date.
 * @author Shivam Mishra
 */

export const formatDate = (date) => {
  if (date === null) return "No due date";
  const parsedDate = new Date(date);

  // Check if the parsed date is valid
  if (!isValid(parsedDate)) {
    return;
  }
  const formattedDate = format(new Date(date), "d MMM yy");
  return formattedDate;
};

/**
 * Checks if time values are invalid (NaN or out of valid range)
 * @author Muskan Thakur
 */
const isInvalidTimeRange = (hoursNum, minutesNum) => {
  return (
    isNaN(hoursNum) ||
    isNaN(minutesNum) ||
    hoursNum < number.ZERO ||
    hoursNum > number.TWENTY_THREE ||
    minutesNum < number.ZERO ||
    minutesNum > number.FIFTY_NINE
  );
};

/**
 * Function to format a time string.
 * @param {string} time - The time string to format (in HH:mm format).
 * @returns {string} - Returns the formatted time string (in hh:mm AM/PM format).
 * @author Shivam Mishra
 */
export const formatTime = (time) => {
  try {
    const [hours, minutes] = time.split(":");
    const hoursNum = parseInt(hours, number.TEN);
    const minutesNum = parseInt(minutes, number.TEN);
    if (isInvalidTimeRange(hoursNum, minutesNum)) {
      return time; 
    }
    const date = new Date();
    date.setHours(hoursNum, minutesNum, number.ZERO, number.ZERO);
    const formattedTime = format(date, "hh:mm aa");
    return formattedTime;
  } catch (e) {
    return time; 
  }
};

/**
 * Function to format a date and time.
 * @param {Date} date - The date to format.
 * @param {string} time - The time string to format (in HH:mm format).
 * @returns {string} - Returns the formatted date and time string.
 * @author Shivam Mishra
 */
export const formatDateTime = (date, time) => {
  const formattedDate = formatDate(date);
  const formattedTime = formatTime(time);
  return `${formattedDate} ${formattedTime}`;
};

// created for front end implementation going to remove in future
export const arrayOfObjects = [
  { id: 1, value: ["Tokyo", "Rio", "Denver", "StockHolm", "Delhi"] },
  { id: 2, value: ["India", "UAE", "RSA", "Italy", "Japan"] },
  { id: 3, value: ["Asia", "Africa", "Australia", "Europe", "North & South America"] },
];

/**
 * Function to format a date and time into 24 hour format .
 * @param {string} time - The time string to format (in HH:mm format).
 * @returns {string} - Returns the formatted date and time string.
 * @author Shivam Mishra
 */
export function convertTo24HourFormat(time) {
  const date = parse(time, "h:mm a", new Date());

  if (!isValid(date)) {
    return format(new Date(), "HH:mm");
  }

  return format(date, "HH:mm");
}

/**
 * Returns an array of time format patterns for parsing time strings
 * @author Muskan Thakur 
 */
export const getTimeFormats = (normalizedTime) => {
  return [
    { pattern: "h:mm a", time: normalizedTime },
    { pattern: "hh:mm a", time: normalizedTime },
    { pattern: "h:mma", time: normalizedTime.replace(/\s+/g, "") },
    { pattern: "hh:mma", time: normalizedTime.replace(/\s+/g, "") },
    { pattern: "h:mm a", time: normalizedTime.toLowerCase() },
    { pattern: "hh:mm a", time: normalizedTime.toLowerCase() },
    { pattern: "h:mma", time: normalizedTime.toLowerCase().replace(/\s+/g, "") },
    { pattern: "hh:mma", time: normalizedTime.toLowerCase().replace(/\s+/g, "") },
  ];
}

/**
 * Function to check if an event listener is attached to an element.
 * @param {HTMLElement} element - The element to check for event listener.
 * @param {string} eventName - The name of the event.
 * @param {Function} handler - The event handler function.
 * @returns {boolean} - Returns true if the element has the specified event listener, false otherwise.
 * @author Shivam Mishra
 */
export const hasEventListener = (element, eventName, handler) => {
  return element && element._eventListeners && element._eventListeners[eventName] && element._eventListeners[eventName].some((listener) => listener === handler);
};

/**
 * Function to parse the inputDateString into a Date object and format it into a time string.
 * @param {string} inputDateString - The string representing the date and time in the format 'MMM dd, yyyy hh:mm a'.
 * @returns {[Date, string]} - Returns an array containing the parsed Date object and the formatted time string.
 * @author Shivam Mishra
 */
export function getDateAndTime(inputDateString) {
  const parsedDate = parse(inputDateString, "dd MMM yy", new Date());
  const dateObject = new Date(parsedDate);
  return dateObject;
}

export const editorIcon = [
  {
    id: 1,
    class: "",
    icon: icon.CLOSE_EDITOR,
  },
  {
    id: 2,
    class: "",
    icon: icon.EDIT_DESCRIPTION,
    title: label.EDIT,
  },
  {
    id: 3,
    class: "unsave-icon",
    icon: icon.SAVE,
    title: label.UNSAVE,
  },
  {
    id: 4,
    class: "",
    icon: icon.SAVE,
    title: label.EXIT_EDIT,
  },
];

/**
 * Function to search users based on a keyword.
 * @param {string} keyword - The keyword to search for.
 * @returns {Array<Object>} - The array of users matching the keyword.
 * @author Shivam Mishra
 */
export function searchUsers(keyword, defaultDetails, assigneeList) {
  keyword = keyword.toLowerCase();
  if (!keyword.length) return assigneeList;

  return defaultDetails?.assigneeList?.filter((assignee) => assignee.label.toLowerCase().includes(keyword) || assignee.Email.toLowerCase().includes(keyword));
}

export const createwebsocketUrl = (url) => {
  if (url) {
    const protocol = splitString(url, ":");
    if (protocol) if (protocol && protocol[0] === "http") return url?.replace(/^http/, "ws");
    if (protocol && protocol[0] === "https") return url?.replace(/^https/, "wss");
  }
};

export const customVariablesClasses = ["custom-dropdown-chip", "custom-admin-chip", "custom-input-chip", "custom-date-chip", "custom-number-chip"];

export const smartChipClasses = ["smart-dropdown-chip", "smart-admin-chip", "smart-input-chip", "smart-date-chip", "smart-number-chip"];

/**
 * Configuration for bottom icons in SmartChip component
 * @author Muskan Thakur
 */
export const getBottomIconsConfig = (icon, tooltip) => [
  { componentName: "DateChip", icon: icon.PLAN_DO_TOMORROW, tooltip: tooltip.SELECT_DATE_TOOLTIP },
  { componentName: "TimeChip", icon: icon.ALARM, tooltip: tooltip.SELECT_TIME_TOOLTIP },
  { componentName: "BookmarkTool", icon: icon.BOOKMARK_TOOL, tooltip: tooltip.ADD_BOOKMARK_TOOLTIP },
];

/**
 * Tab labels configuration for SmartChip component
 * @author Muskan Thakur 
 */
export const getTabLabels = (label) => ({
  chips: label.CHIPS,
  users: label.USER,
  taskId: label.TASK_ID,
});

/**
 * Gets active variable IDs from the editor content
 * @param {EditorView} view - The editor view
 * @returns {Set} Set of active variable IDs
 * @author Abhishek Dhillon
 */
export const getActiveVariableIds = (html) => {
  try {
    if (!html) return new Set();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const spans = doc.querySelectorAll('span[id^="ss_"]');
    return new Set(
      Array.from(spans)
        .map((span) => (SMART_CHIP_REGEX.test(span.id) ? span.id : null))
        .filter(Boolean)
    );
  } catch {
    return new Set();
  }
};

/**
 * Gets chip content from editor by ID using editorRef
 * @author Muskan Thakur
 */
export const getChipContent = (editorRef, chipId) => {
  if (!editorRef?.current || !chipId) return null;
  
  try {
    const view = editorRef.current.state?.view || editorRef.current.view;
    if (!view) return null;
    
    const { state } = view;
    const { doc } = state;
    
    let content = null;
    doc.descendants((node) => {
      if (node.attrs?.id === chipId && node.type.name === "nonEditable") {
        content = node.textContent || "";
        return false; 
      }
      return true;
    });
    
    return content;
  } catch (error) {
    return null;
  }
};

/**
 * Updates chip content in editor by ID using editorRef
 * @author Muskan Thakur
 */
export const updateChipContent = (editorRef, chipId, newText, newAttrs = {}) => {
  if (!editorRef?.current || !chipId) return false;
  
  try {
    const view = editorRef.current.state?.view || editorRef.current.view;
    if (!view) return false;
    
    const { state, dispatch } = view;
    const { doc, tr, schema } = state;
    
    let updated = false;
    doc.descendants((node, pos) => {
      if (node.attrs?.id === chipId && node.type.name === "nonEditable") {
        const updatedAttrs = {
          ...node.attrs,
          ...newAttrs,
        };
        
        const updatedNode = schema.nodes.nonEditable.create(
          updatedAttrs,
          schema.text(newText)
        );
        
        dispatch(tr.replaceWith(pos, pos + node.nodeSize, updatedNode));
        updated = true;
        return false; 
      }
      return true;
    });
    
    return updated;
  } catch (error) {
    return false;
  }
};
